/**
 * @file schemas.ts
 * @brief Dropdown option constants for the Super Admin → Settings feature.
 *
 * @details
 * AI pipeline keys (ASR/MT/TTS providers + models, realtime provider) are
 * edited via AiConfigurationCard using these guided pickers — there is no
 * generic key/value editor anymore.
 *
 * @references
 * - FRONTEND/src/screens/SuperAdmin/tabs/ModelsTab.tsx (mobile parity)
 * - BACKEND/src/config/env.js (runtime consumers)
 *
 * @author DES
 * @date 19-06-2026
 */

export const ASR_PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI" },
  { value: "deepgram", label: "Deepgram (Fastest)" },
  { value: "soniox", label: "Soniox (real-time)" },
  { value: "auto", label: "Auto (Hybrid)" },
] as const;

export const REALTIME_PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI (gpt-realtime)" },
  { value: "deepgram", label: "Deepgram" },
  { value: "disabled", label: "Disabled" },
] as const;

export const MT_PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI (GPT Series)" },
  // Soniox translates inside the Soniox speech-to-text call (16-10-2026).
  { value: "soniox", label: "Soniox (with Soniox ASR)" },
] as const;

/** Help under the translation picker (DES brief 16-10-2026). */
export const MT_SONIOX_HINT =
  "Soniox translation only works when ASR is Soniox; otherwise OpenAI is used.";

export const MT_MODEL_OPTIONS = [
  {
    value: "gpt-4o-mini",
    label: "GPT-4o Mini (Cost-Effective / High Speed)",
  },
  { value: "gpt-4o", label: "GPT-4o (High Quality / Complex)" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini (Balanced)" },
  // gpt-4.1-nano removed 24-10-2026 (W15): OpenAI answers 404 (aiPipeline RETIRED_MODELS).
] as const;

export const TTS_PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI (Premium Voices)" },
  { value: "deepgram", label: "Deepgram (Fastest)" },
  { value: "auto", label: "Auto (Hybrid)" },
] as const;

export const TTS_MODEL_OPTIONS = [
  {
    value: "gpt-4o-mini-tts-2025-12-15",
    label: "GPT-4o Mini TTS (Expressive Voice)",
  },
  { value: "tts-1", label: "TTS-1 (Standard Fast)" },
  { value: "tts-1-hd", label: "TTS-1 HD (High Fidelity)" },
] as const;

// ----------------------------------------------------------------------
// Switchboard fallback catalog (11-07-2026) — mirrors
// BACKEND/src/infrastructure/ai/catalog.mjs. Used ONLY while
// GET /api/admin/ai/catalog is loading or errored, so the cascading
// pickers always have a shape to render. The live endpoint is the source
// of truth (its `configured` flags reflect the backend's actual env keys).
// ----------------------------------------------------------------------
import type { AiCatalog } from "./api";

export const FALLBACK_AI_CATALOG: AiCatalog = {
  TTS: {
    openai: {
      label: "OpenAI (Premium Voices)",
      universal: true,
      languages: null,
      configured: true,
      models: [
        { value: "gpt-4o-mini-tts-2025-12-15", label: "GPT-4o Mini TTS (Expressive Voice)" },
        { value: "tts-1", label: "TTS-1 (Standard Fast)" },
        { value: "tts-1-hd", label: "TTS-1 HD (High Fidelity)" },
      ],
    },
    deepgram: {
      label: "Deepgram (Fastest)",
      universal: false,
      languages: null,
      configured: true,
      models: [
        { value: "aura-asteria-en", label: "Aura — Asteria (US Female, default)" },
        { value: "aura-athena-en", label: "Aura — Athena (UK Female)" },
        { value: "aura-2-thalia-en", label: "Aura-2 — Thalia (US Female, newest)" },
      ],
    },
    google: {
      label: "Google Cloud TTS",
      universal: false,
      languages: null,
      configured: false,
      models: [
        { value: "chirp3-hd", label: "Chirp 3 HD (Best Quality)" },
        { value: "neural2", label: "Neural2 (Balanced)" },
        { value: "wavenet", label: "WaveNet (Classic Premium)" },
        { value: "standard", label: "Standard (Cheapest)" },
      ],
    },
    azure: {
      label: "Azure AI Speech",
      universal: false,
      languages: null,
      configured: false,
      models: [
        { value: "neural", label: "Neural voices (Standard)" },
        { value: "neural-hd", label: "HD voices (Highest Quality)" },
      ],
    },
    soniox: {
      label: "Soniox",
      universal: false,
      languages: null,
      configured: false,
      models: [{ value: "tts-rt-v2", label: "TTS RT v2" }],
    },
  },
  ASR: {
    openai: {
      label: "OpenAI",
      universal: true,
      languages: null,
      configured: true,
      models: [
        { value: "whisper-1", label: "Whisper-1 (Proven Multilingual)" },
        { value: "gpt-4o-mini-transcribe", label: "GPT-4o Mini Transcribe (Newer, Cheaper)" },
      ],
    },
    deepgram: {
      label: "Deepgram (Fastest)",
      universal: false,
      languages: null,
      configured: true,
      models: [
        { value: "nova-3", label: "Nova-3 (Best, Code-Switching)" },
        { value: "nova-2", label: "Nova-2 (Stable Fallback)" },
      ],
    },
    soniox: {
      label: "Soniox (real-time)",
      universal: false,
      languages: null,
      configured: false,
      models: [{ value: "stt-rt-v5", label: "stt-rt-v5 (Real-time v5)" }],
    },
  },
  LLM: {
    openai: {
      label: "OpenAI (GPT)",
      universal: true,
      languages: null,
      configured: true,
      models: [
        { value: "gpt-4.1", label: "GPT-4.1 (Deep Analysis, Fact Check)" },
        { value: "gpt-4.1-mini", label: "GPT-4.1 Mini (Balanced, Default)" },
        { value: "gpt-4o-mini", label: "GPT-4o Mini (Cheapest)" },
        { value: "gpt-4o", label: "GPT-4o (Legacy High Quality)" },
      ],
    },
    anthropic: {
      label: "Anthropic (Claude)",
      universal: false,
      languages: null,
      configured: false,
      models: [
        { value: "claude-sonnet-5", label: "Claude Sonnet 5 (Deep Analysis)" },
        { value: "claude-opus-4-8", label: "Claude Opus 4.8 (Highest Quality)" },
        { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (Fast, Cheap)" },
      ],
    },
    google: {
      label: "Google (Gemini)",
      universal: false,
      languages: null,
      configured: false,
      models: [
        { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Deep Analysis)" },
        { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (Fast, Cheap)" },
      ],
    },
  },
};
